import type { AvanceDescarga } from "./descarga.js";
import type { FalloDeVisor } from "./errores.js";

export type Eje = "x" | "y" | "z";

export interface AccionesToolbar {
  encuadrar(): void;
  alternarGrid(activa: boolean): void;
  alternarSeccion(activa: boolean): void;
  elegirEje(eje: Eje): void;
}

/** Iconos SVG inline. Sin libreria de iconos: son cuatro trazos. */
const ICONOS = {
  encuadrar:
    '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 9V5.5A1.5 1.5 0 0 1 5.5 4H9"/><path d="M20 9V5.5A1.5 1.5 0 0 0 18.5 4H15"/><path d="M4 15v3.5A1.5 1.5 0 0 0 5.5 20H9"/><path d="M20 15v3.5A1.5 1.5 0 0 1 18.5 20H15"/><rect x="9.5" y="9.5" width="5" height="5" rx="1"/></svg>',
  grid: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 12h18M12 3v18M3 7.5h18M3 16.5h18M7.5 3v18M16.5 3v18"/></svg>',
  seccion:
    '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 16.5 12 12l8 4.5"/><path d="M4 16.5v-4L12 8l8 4.5v4"/><path d="M2 20h20"/></svg>',
  error:
    '<svg class="tarjeta__icono" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 8v5"/><path d="M12 16.5h.01"/><path d="M10.3 3.9 2.5 17.4A2 2 0 0 0 4.2 20.4h15.6a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z"/></svg>',
} as const;

function boton(clase: string, etiqueta: string, contenido: string): HTMLButtonElement {
  const b = document.createElement("button");
  b.type = "button";
  b.className = clase;
  b.title = etiqueta;
  b.setAttribute("aria-label", etiqueta);
  b.innerHTML = contenido;
  return b;
}

export class Ui {
  private contexto: HTMLElement | null = null;
  private readonly raiz: HTMLElement;
  private capa: HTMLElement | null = null;
  private relleno: HTMLElement | null = null;
  private barra: HTMLElement | null = null;
  private toolbar: HTMLElement | null = null;
  private readonly botonesEje = new Map<Eje, HTMLButtonElement>();
  private grupoEjes: HTMLElement | null = null;

  constructor(raiz: HTMLElement) {
    this.raiz = raiz;
  }

  // ------------------------------------------------------------------ capas

  mostrarCargando(titulo: string, detalle: string): void {
    // Solo se reemplaza la capa anterior. Vaciar la raiz se llevaria por delante la
    // toolbar y el overlay de metricas, que son hermanos y tienen otro ciclo de vida.
    this.capa?.remove();
    const capa = document.createElement("div");
    capa.className = "capa";
    capa.setAttribute("role", "status");
    capa.setAttribute("aria-live", "polite");
    capa.innerHTML = `
      <div class="tarjeta">
        <p class="tarjeta__titulo">${titulo}</p>
        <p class="tarjeta__texto"></p>
        <div class="barra barra--indeterminada"><div class="barra__relleno"></div></div>
      </div>`;
    const texto = capa.querySelector(".tarjeta__texto");
    if (texto !== null) texto.textContent = detalle;
    this.raiz.append(capa);
    this.capa = capa;
    this.barra = capa.querySelector(".barra");
    this.relleno = capa.querySelector(".barra__relleno");
  }

  actualizarDetalle(detalle: string): void {
    const texto = this.capa?.querySelector(".tarjeta__texto");
    if (texto !== null && texto !== undefined) texto.textContent = detalle;
  }

  actualizarAvance(avance: AvanceDescarga): void {
    if (this.barra === null || this.relleno === null) return;
    if (avance.total === null) {
      // Sin Content-Length no se puede dar un porcentaje honesto.
      this.barra.classList.add("barra--indeterminada");
      this.actualizarDetalle(`Descargando ${formatoMb(avance.recibidos)}`);
      return;
    }
    this.barra.classList.remove("barra--indeterminada");
    const pct = Math.min(100, (avance.recibidos / avance.total) * 100);
    this.relleno.style.width = `${pct.toFixed(1)}%`;
    this.actualizarDetalle(
      `Descargando ${formatoMb(avance.recibidos)} de ${formatoMb(avance.total)}`,
    );
  }

  /** Barra determinada al 0-100 % para etapas que no son descarga. */
  fijarAvance(fraccion: number, detalle: string): void {
    if (this.barra === null || this.relleno === null) return;
    this.barra.classList.remove("barra--indeterminada");
    this.relleno.style.width = `${(Math.min(1, Math.max(0, fraccion)) * 100).toFixed(1)}%`;
    this.actualizarDetalle(detalle);
  }

  mostrarError(fallo: FalloDeVisor): void {
    this.capa?.remove();
    const capa = document.createElement("div");
    capa.className = "capa";
    capa.setAttribute("role", "alert");
    capa.innerHTML = `
      <div class="tarjeta tarjeta--error">
        ${ICONOS.error}
        <p class="tarjeta__titulo"></p>
        <p class="tarjeta__texto"></p>
      </div>`;
    const titulo = capa.querySelector(".tarjeta__titulo");
    const texto = capa.querySelector(".tarjeta__texto");
    if (titulo !== null) titulo.textContent = fallo.texto.titulo;
    if (texto !== null) texto.textContent = fallo.texto.detalle;
    this.raiz.append(capa);
    this.capa = capa;
    this.barra = null;
    this.relleno = null;
  }

  ocultarCapa(): void {
    this.capa?.remove();
    this.capa = null;
    this.barra = null;
    this.relleno = null;
  }

  // --------------------------------------------------------------- contexto

  /**
   * Nombre del proyecto y fecha, para que quien abra el link sepa que esta mirando.
   *
   * Discreta a proposito y sin nada mas: ni logo, ni quien lo envio, ni menus. El §5 del
   * SPEC es explicito en que el visor es una vista, y esto es lo minimo para que un
   * modelo deje de ser un objeto flotante sin identificar.
   */
  montarContexto(proyecto: string | undefined, publicadoEn: number | undefined): void {
    if (proyecto === undefined && publicadoEn === undefined) return;

    const caja = document.createElement("div");
    caja.className = "contexto";

    if (proyecto !== undefined) {
      const nombre = document.createElement("p");
      nombre.className = "contexto__proyecto";
      // textContent y no innerHTML: la etiqueta la escribe quien publica y acaba en el
      // navegador de un tercero.
      nombre.textContent = proyecto;
      caja.append(nombre);
    }

    if (publicadoEn !== undefined) {
      const fecha = document.createElement("p");
      fecha.className = "contexto__fecha";
      // Locale fijo en espanol, no el del navegador de quien mira: el resto del visor
      // esta en espanol y una fecha en ingles al lado desentona. La zona horaria si es
      // la del dispositivo, que es lo correcto.
      const d = new Date(publicadoEn * 1000);
      fecha.textContent = d.toLocaleDateString("es-PE", {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
      // La hora exacta en el title, por si hace falta el dato preciso.
      fecha.title = d.toLocaleString("es-PE");
      caja.append(fecha);
    }

    this.raiz.append(caja);
    this.contexto = caja;
  }

  /** Simetrico a `quitarToolbar`: si algo falla despues, no se queda una etiqueta huerfana. */
  quitarContexto(): void {
    this.contexto?.remove();
    this.contexto = null;
  }

  // --------------------------------------------------------------- toolbar

  montarToolbar(acciones: AccionesToolbar): void {
    const barra = document.createElement("div");
    barra.className = "toolbar";
    barra.setAttribute("role", "toolbar");
    barra.setAttribute("aria-label", "Herramientas del visor");

    const encuadrar = boton("toolbar__boton", "Encuadrar todo (F)", ICONOS.encuadrar);
    encuadrar.addEventListener("click", () => acciones.encuadrar());

    const grid = boton("toolbar__boton", "Mostrar rejilla", ICONOS.grid);
    grid.setAttribute("aria-pressed", "true");
    grid.addEventListener("click", () => {
      const activa = grid.getAttribute("aria-pressed") !== "true";
      grid.setAttribute("aria-pressed", String(activa));
      acciones.alternarGrid(activa);
    });

    const seccion = boton("toolbar__boton", "Plano de seccion", ICONOS.seccion);
    seccion.setAttribute("aria-pressed", "false");
    seccion.addEventListener("click", () => {
      const activa = seccion.getAttribute("aria-pressed") !== "true";
      seccion.setAttribute("aria-pressed", String(activa));
      this.mostrarEjes(activa);
      acciones.alternarSeccion(activa);
    });

    const ejes = document.createElement("div");
    ejes.className = "toolbar__ejes";
    ejes.hidden = true;
    for (const eje of ["x", "y", "z"] as const) {
      const b = document.createElement("button");
      b.type = "button";
      b.className = "toolbar__eje";
      b.textContent = eje.toUpperCase();
      b.title = `Cortar en el eje ${eje.toUpperCase()}`;
      b.setAttribute("aria-pressed", String(eje === "y"));
      b.addEventListener("click", () => {
        this.fijarEje(eje);
        acciones.elegirEje(eje);
      });
      ejes.append(b);
      this.botonesEje.set(eje, b);
    }

    const sep = document.createElement("div");
    sep.className = "toolbar__sep";

    barra.append(encuadrar, grid, sep, seccion, ejes);
    this.raiz.append(barra);

    this.toolbar = barra;
    this.grupoEjes = ejes;
  }

  private mostrarEjes(visible: boolean): void {
    if (this.grupoEjes !== null) this.grupoEjes.hidden = !visible;
  }

  private fijarEje(eje: Eje): void {
    for (const [clave, b] of this.botonesEje) {
      b.setAttribute("aria-pressed", String(clave === eje));
    }
  }

  quitarToolbar(): void {
    this.toolbar?.remove();
    this.toolbar = null;
    this.grupoEjes = null;
    this.botonesEje.clear();
  }
}

function formatoMb(bytes: number): string {
  return `${(bytes / 1048576).toFixed(1)} MB`;
}
